import React from 'react'
import PropTypes from 'prop-types'
import { resolveCssVariable, pickAccessibleTextColor } from "../../../../utils"
import './WingsBadge.css'

/**
 * WingsBadge Component
 * 
 * A badge component with bold Montserrat text and colored background, typically used for names or identifiers.
 * Text is automatically uppercase via CSS.
 * 
 * @component
 * @example
 * <WingsBadge variant="primary">John Doe</WingsBadge>
 * <WingsBadge variant="secondary">Admin</WingsBadge>
 */
const WingsBadge = ({
  children,
  variant = 'primary',
  className = '',
  prefix = '',
  size = 'narrow',
  backgroundColor,
  style={},
  ...props
}) => {
  const badgeClasses = [
    'wings-badge',
    `wings-badge-${variant}`,
    `wings-badge-${size}`,
    className
  ].filter(Boolean).join(' ')

  if (backgroundColor) {
    style.backgroundColor = backgroundColor
    style.color = pickAccessibleTextColor(resolveCssVariable(backgroundColor) || "#000000")
  }

  return (
    <div className={badgeClasses} style={style} {...props}>
      {prefix}{children}
    </div>
  )
}

WingsBadge.propTypes = {
  /** Content to display in the badge */
  children: PropTypes.node.isRequired,
  /** Visual style variant of the badge */
  variant: PropTypes.oneOf([
    'primary',
    'secondary',
    'tertiary',
    'soft-skill',
    'hard-skill',
    'teal',
    'purple',
    'tal-e',
    'on-dark',
  ]),
  /** Additional CSS classes */
  className: PropTypes.string,
  /** Optional prefix text (e.g., "—") */
  prefix: PropTypes.string,
  /** Size variant */
  size: PropTypes.oneOf([
    'default',
    'narrow',
  ]),
  /** Hex code or css variable. If provided, specifies badge color.  */
  backgroundColor: PropTypes.string
}

export default WingsBadge
